import { useState } from "react";
import { Smartphone, Mail, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function MySabbatAppCTA() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("insert_email_signup", {
        signup_email: email,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };
      if (result.success) {
        setIsSubscribed(true);
        toast.success("Du er tilmeldt! Vi giver besked når appen er klar.");
      } else {
        if (result.error === "Email already registered") {
          toast.info("Denne email er allerede tilmeldt ventelisten.");
        } else {
          toast.error(result.error || "Der opstod en fejl. Prøv igen.");
        }
      }
    } catch (error) {
      toast.error("Der opstod en fejl. Prøv igen senere.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className="my-12 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 border border-primary/20 rounded-2xl p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-lp-green/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-lp-green" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">
          Du er på ventelisten! 🎉
        </h3>
        <p className="text-muted-foreground">
          Vi giver dig besked så snart MySabbat-appen er klar til download.
        </p>
      </div>
    );
  }

  return (
    <div className="my-12 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 border border-primary/20 rounded-2xl p-8">
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Icon/illustration */}
        <div className="flex-shrink-0">
          <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center">
            <Smartphone className="w-10 h-10 text-primary" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-xl font-bold text-foreground mb-2">
            📱 Planlæg dit sabbatår med MySabbat
          </h3>
          <p className="text-muted-foreground mb-4">
            Snart kan du holde styr på dit sabbatår med vores app – 
            budgetplanlægger, tjeklister, rejsetips og meget mere samlet ét sted.
          </p>

          {/* Email signup form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto md:mx-0">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="email"
                placeholder="Din email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
            <Button type="submit" disabled={isLoading} className="bg-primary hover:bg-primary/90">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Giv mig besked"
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-3">
            Vi sender kun én email når appen er klar. Ingen spam.
          </p>
        </div>
      </div>
    </div>
  );
}

export default MySabbatAppCTA;
